
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { useState, useEffect, useRef } from "react";

export const useVideoInteraction = (videoId: string) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [hasLiked, setHasLiked] = useState(false);
  const isLikingRef = useRef(false);
  const isMounted = useRef(true);

  // Set up cleanup function
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Check if user has already liked this video
  const { data: interaction, isPending } = useQuery({
    queryKey: ['video-interaction', videoId, user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      
      const { data, error } = await supabase
        .from('video_interactions')
        .select('*')
        .eq('video_id', videoId)
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) {
        console.error('Error fetching video interaction:', error);
        return null;
      }

      console.log(`Video interaction check for ${videoId}:`, data ? 'Found' : 'Not found');
      return data;
    },
    enabled: !!user?.id,
  });
  
  // Update local like state when query data changes
  useEffect(() => {
    if (isMounted.current) {
      setHasLiked(!!interaction?.liked_at);
    }
  }, [interaction]);

  const likeMutation = useMutation({
    mutationFn: async () => {
      if (!user?.id) {
        throw new Error('User must be logged in to like videos');
      }
      
      // Prevent liking if already in progress or already liked
      if (isLikingRef.current || hasLiked || !!interaction?.liked_at) {
        console.log('Video like operation prevented:', {
          reason: isLikingRef.current ? 'Like in progress' : 'Already liked',
          videoId
        });
        return;
      }

      // Set flag to prevent duplicate operations
      isLikingRef.current = true;

      try {
        // Set local state immediately for responsive UI
        if (isMounted.current) {
          setHasLiked(true);
        }
        
        // Create new interaction with like
        const { error } = await supabase
          .from('video_interactions')
          .upsert({
            id: interaction?.id,
            video_id: videoId,
            user_id: user.id,
            liked_at: new Date().toISOString()
          });

        if (error) throw error;
        
        console.log(`Successfully liked video ${videoId}`);
      } catch (error) {
        // Reset state on error
        if (isMounted.current) {
          setHasLiked(false);
        }
        console.error('Error in video like mutation:', error);
        throw error;
      } finally {
        // Reset flag after operation with a small delay
        setTimeout(() => {
          if (isMounted.current) {
            isLikingRef.current = false;
          }
        }, 500);
      }
    },
    onSuccess: () => {
      // Use a focused invalidation to prevent excessive refetching
      queryClient.invalidateQueries({ 
        queryKey: ['video-interaction', videoId, user?.id],
        exact: true
      });
    },
    onError: (error) => {
      console.error('Error in like mutation:', error);
      toast({
        title: "Error",
        description: "Failed to update like status. Please try again.",
        variant: "destructive"
      });
      if (isMounted.current) {
        setHasLiked(false);
      }
    }
  });

  return {
    isLiked: hasLiked,
    isPending: isPending || likeMutation.isPending,
    toggleLike: () => {
      // Only allow liking if not already liked
      if (hasLiked || !!interaction?.liked_at || isLikingRef.current) {
        console.log('Video like prevented: already liked or in progress');
        return;
      }
      likeMutation.mutate();
    }
  };
};
